import React, { useRef, useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  Stack,
  IconButton,
  Paper,
  Checkbox,
  ListItemText,
  useTheme,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
} from "@mui/material";
import { useMediaQuery } from "@mui/material";

// Iconos
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import BusinessIcon from "@mui/icons-material/Business";
import AssignmentIcon from "@mui/icons-material/Assignment";
import StorefrontIcon from "@mui/icons-material/Storefront";
import PeopleIcon from "@mui/icons-material/People";
import EventNoteIcon from "@mui/icons-material/EventNote";
import DescriptionIcon from "@mui/icons-material/Description";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import RepeatIcon from "@mui/icons-material/Repeat";
import DownloadIcon from "@mui/icons-material/Download";
import SettingsIcon from "@mui/icons-material/Settings";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";

// Contexto
import { useMetricas } from "../../../context/MetricasContext";
import { useAuth } from "../../../context/AuthContext";
const AccionesIngresoRapido = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const scrollContainerRef = useRef(null);
  const selectorRef = useRef(null);
  const selectorButtonRef = useRef(null); // Ref para el botón de configuración

  const { user } = useAuth();
  const { metricas } = useMetricas();
  const [rangoTiempo, setRangoTiempo] = useState("todo");
  const [openSelector, setOpenSelector] = useState(false);

  const ESTADOS_VALIDOS = [
    "leads",
    "nutricion",
    "interesado",
    "agendado",
    "seguimiento",
    "cotizacion",
    "contrato",
    "ganado",
    "perdido",
  ];

  // ---------------- LÓGICA DE FILTRADO POR TIEMPO ----------------
  const metricasFiltradas = useMemo(() => {
    if (!metricas || !Array.isArray(metricas)) return [];
    if (rangoTiempo === "todo") return metricas;

    const hoy = new Date();
    const dias = parseInt(rangoTiempo);
    const fechaLimite = new Date();
    fechaLimite.setDate(hoy.getDate() - dias);

    return metricas.filter((item) => {
      const fechaItem = new Date(item.fecha_creacion || item.created_at);
      return fechaItem >= fechaLimite;
    });
  }, [metricas, rangoTiempo]);



  // ---------------- CALCULO DE ESTADOS ----------------
  const metricasEstados = useMemo(() => {
    const result = {};
    ESTADOS_VALIDOS.forEach((e) => (result[e] = 0));
    metricasFiltradas.forEach((item) => {
      const estado = item.estado_contacto;
      if (ESTADOS_VALIDOS.includes(estado)) {
        result[estado] += 1;
      }
    });
    return result;
  }, [metricasFiltradas]);

  // Determinar si el usuario actual es el de presentación (ID 1500)
  // Se consideran diferentes posibles nombres de campo para el ID, según cómo el JWT sea decodificado.
  const isPresentationUser =
    user?.id === 1500 ||
    user?.id === "1500" ||
    user?.id_usuario === 1500 ||
    user?.id_usuario === "1500" ||
    user?.user_id === 1500 ||
    user?.user_id === "1500";
  const metricData = useMemo(
    () => {
      const data = [
        {
          id: "ganancias",
          IconComponent: AttachMoneyIcon,
          title: "Ganancias Totales",
          value: isPresentationUser ? "$1,249,253" : 0,
          color: "#4CAF50",
        },
        {
          id: "todos",
          IconComponent: PeopleIcon,
          title: "Todos los Contactos",
          value: isPresentationUser ? "15,420" : metricasFiltradas.length,
          color: "#1976D2",
        },
        {
          id: "leads",
          IconComponent: PeopleIcon,
          title: "Leads",
          value: isPresentationUser ? "3,200" : metricasEstados.leads,
          color: "#2196F3",
        },
        {
          id: "nutricion",
          IconComponent: RepeatIcon,
          title: "Nutrición",
          value: isPresentationUser ? "1,800" : metricasEstados.nutricion,
          color: "#673AB7",
        },
        {
          id: "interesado",
          IconComponent: AssignmentIcon,
          title: "Interesados",
          value: isPresentationUser ? "950" : metricasEstados.interesado,
          color: "#FF5722",
        },
        {
          id: "agendado",
          IconComponent: EventNoteIcon,
          title: "Agendados",
          value: isPresentationUser ? "450" : metricasEstados.agendado,
          color: "#009688",
        },
        {
          id: "cotizacion",
          IconComponent: DescriptionIcon,
          title: "Cotización",
          value: isPresentationUser ? "320" : metricasEstados.cotizacion,
          color: "#795548",
        },
        {
          id: "seguimiento",
          IconComponent: StorefrontIcon,
          title: "Seguimiento",
          value: isPresentationUser ? "210" : metricasEstados.seguimiento,
          color: "#FFC107",
        },
        {
          id: "contrato",
          IconComponent: StorefrontIcon,
          title: "Contrato",
          value: isPresentationUser ? "180" : metricasEstados.contrato,
          color: "#FFC107",
        },
        {
          id: "ganados",
          IconComponent: ShoppingCartIcon,
          title: "Ganados",
          value: isPresentationUser ? "150" : metricasEstados.ganado,
          color: "#4CAF50",
        },
        {
          id: "perdidos",
          IconComponent: BusinessIcon,
          title: "Perdidos",
          value: isPresentationUser ? "40" : metricasEstados.perdido,
          color: "#E91E63",
        },
      ];
      return data;
    },
    [metricasEstados, metricasFiltradas.length, isPresentationUser]
  );

  const [metricasSeleccionadas, setMetricasSeleccionadas] = useState([]);
  const [metricasVisibles, setMetricasVisibles] = useState([]);

  // Inicializar visibilidad
  useEffect(() => {
    const seleccionadas = metricData.map((_, i) => i < 6);
    setMetricasSeleccionadas(seleccionadas);
    setMetricasVisibles(metricData.filter((_, i) => i < 6));
  }, [metricData]);

  // ---------------- CERRAR AL CLICAR FUERA (HandleClickOutside) ----------------
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        selectorRef.current &&
        !selectorRef.current.contains(event.target) &&
        selectorButtonRef.current &&
        !selectorButtonRef.current.contains(event.target)
      ) {
        setOpenSelector(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ---------------- LÓGICA DE NAVEGACIÓN Y SCROLL ----------------
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  const checkScroll = () => {
    const container = scrollContainerRef.current;
    if (container) {
      setShowLeftArrow(container.scrollLeft > 10);
      setShowRightArrow(
        container.scrollLeft <
        container.scrollWidth - container.clientWidth - 10
      );
    }
  };

  const scroll = (direction) => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: direction === "left" ? -300 : 300,
        behavior: "smooth",
      });
    }
  };

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      checkScroll();
      container.addEventListener("scroll", checkScroll);
      window.addEventListener("resize", checkScroll);
    }
    return () => {
      if (container) container.removeEventListener("scroll", checkScroll);
      window.removeEventListener("resize", checkScroll);
    };
  }, [metricasVisibles]);

  const toggleMetrica = (index) => {
    const nuevasSeleccionadas = [...metricasSeleccionadas];
    nuevasSeleccionadas[index] = !nuevasSeleccionadas[index];
    setMetricasSeleccionadas(nuevasSeleccionadas);
    setMetricasVisibles(metricData.filter((_, i) => nuevasSeleccionadas[i]));
  };

  // ---------------- TARJETA ----------------
  const MetricCard = ({ IconComponent, title, value, color }) => (
    <Paper
      elevation={2}
      sx={{
        p: 2,
        borderRadius: 3,
        border: `1px solid ${color}40`,
        minWidth: isMobile ? 200 : 240,
        flexShrink: 0,
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
        background: "linear-gradient(180deg, #FFFFFF 0%, #F9F9F9 100%)",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Box
          sx={{
            p: 0.5,
            borderRadius: 1,
            backgroundColor: `${color}15`,
            display: "flex",
          }}
        >
          <IconComponent sx={{ fontSize: 18, color: color }} />
        </Box>
        <Typography
          variant="caption"
          sx={{
            color: "#666",
            fontWeight: 600,
            textTransform: "uppercase",
            letterSpacing: 0.5,
          }}
        >
          {title}
        </Typography>
      </Box>
      <Box>
        {value === null || value === undefined ? (
          <CircularProgress size={20} />
        ) : (
          <Typography variant="h4" sx={{ fontWeight: 800, color: "#2D2D2D" }}>
            {value}
          </Typography>
        )}
      </Box>
    </Paper>
  );

  return (
    <Box sx={{ width: "100%" }}>
      <Stack
        direction={isMobile ? "column" : "row"}
        justifyContent="space-between"
        alignItems={isMobile ? "flex-start" : "center"}
        spacing={2}
        sx={{ mb: 2, px: 1 }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#211E26' }}>
          {isMobile ? "Métricas de desempeño (Kpis)" : "Panel de Métricas Real-Time"}
        </Typography>

        <Stack direction="row" spacing={1} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <Select
              value={rangoTiempo}
              onChange={(e) => setRangoTiempo(e.target.value)}
              variant="outlined"
              displayEmpty
              sx={{
                borderRadius: 2,
                fontSize: "0.85rem",
                height: 36,
                backgroundColor: "#fff",
              }}
              startAdornment={
                <CalendarTodayIcon
                  sx={{ fontSize: 16, mr: 1, color: "gray" }}
                />
              }
            >
              <MenuItem value="7">7 Días</MenuItem>
              <MenuItem value="15">15 Días</MenuItem>
              <MenuItem value="30">1 Mes</MenuItem>
              <MenuItem value="365">1 Año</MenuItem>
              <MenuItem value="todo">Todo</MenuItem>
            </Select>
          </FormControl>

          <Button
            ref={selectorButtonRef}
            startIcon={<SettingsIcon />}
            variant="outlined"
            size="small"
            onClick={() => setOpenSelector((prev) => !prev)}
            sx={{ borderRadius: 2, textTransform: "none", height: 36 }}
          >
            Configurar
          </Button>

          <IconButton
            color="primary"
            sx={{ backgroundColor: "#f0f0f0", height: 36, width: 36 }}
          >
            <DownloadIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Stack>

      <Box sx={{ position: "relative" }}>
        {!isMobile && showLeftArrow && (
          <IconButton
            onClick={() => scroll("left")}
            sx={{
              position: "absolute",
              left: -10,
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 10,
              backgroundColor: "white",
              boxShadow: 2,
            }}
          >
            <ArrowBackIosNewIcon fontSize="small" />
          </IconButton>
        )}

        <Box
          ref={scrollContainerRef}
          sx={{
            display: "flex",
            overflowX: "auto",
            gap: 2,
            scrollBehavior: "smooth",
            py: 1,
            px: 1,
            "&::-webkit-scrollbar": { display: "none" },
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            WebkitOverflowScrolling: "touch",
          }}
        >
          {metricasVisibles.map((metric, i) => (
            <MetricCard key={i} {...metric} />
          ))}
        </Box>

        {!isMobile && showRightArrow && (
          <IconButton
            onClick={() => scroll("right")}
            sx={{
              position: "absolute",
              right: -10,
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 10,
              backgroundColor: "white",
              boxShadow: 2,
            }}
          >
            <ArrowForwardIosIcon fontSize="small" />
          </IconButton>
        )}

        {/* SELECTOR DE MÉTRICAS VISIBLES CON LÓGICA DE CIERRE MEJORADA */}
        {openSelector && (
          <Paper
            ref={selectorRef}
            sx={{
              position: "absolute",
              top: 40, // Ajustado para que aparezca debajo de los botones
              right: 45,
              p: 2,
              minWidth: 220,
              zIndex: 2000,
              boxShadow: 4,
              borderRadius: 2,
              maxHeight: 300,
              overflowY: "auto",
            }}
          >
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
              Visibilidad de métricas
            </Typography>
            {metricData.map((metrica, index) => (
              <Stack key={index} direction="row" alignItems="center">
                <Checkbox
                  size="small"
                  checked={metricasSeleccionadas[index]}
                  onChange={() => toggleMetrica(index)}
                />
                <ListItemText
                  primary={metrica.title}
                  primaryTypographyProps={{ fontSize: "0.85rem" }}
                />
              </Stack>
            ))}
          </Paper>
        )}
      </Box>

    </Box>
  );
};

export default AccionesIngresoRapido;



